import React, { Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import KpiCard from './shared/KpiCard';
import StatusBadge from './shared/StatusBadge';
import { formatNum, CHART_COLORS } from './shared/formatters';
import AuthService from '../AuthService';
import constants from '../../config';
import { resolveCardImage } from '../../utils/resolveImage';

var Auth = new AuthService();

function QuickStat(props) {
  return (
    <div className="tw-flex tw-items-center tw-gap-3">
      <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: props.color + '18' }}>
        <i className={'fa ' + props.icon} style={{ color: props.color, fontSize: 13 }} />
      </div>
      <div className="tw-flex-1 tw-min-w-0">
        <div className="tw-text-xs tw-text-[#999]">
          {props.label}
          {props.tooltip && <span className="tw-relative tw-inline-block tw-ml-1 tw-group">
            <i className="fa fa-info-circle" style={{ fontSize: 10, color: '#bbb', cursor: 'help' }} />
            <span className="tw-absolute tw-bottom-full tw-left-1/2 tw-mb-1.5 tw-hidden group-hover:tw-block tw-whitespace-nowrap tw-text-[10px] tw-text-white tw-px-2 tw-py-1 tw-rounded tw-z-50" style={{ backgroundColor: '#333', transform: 'translateX(-50%)' }}>{props.tooltip}</span>
          </span>}
        </div>
        <div className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{props.value}</div>
      </div>
    </div>
  );
}

class AdminOverview extends Component {
  constructor(props) {
    super(props);
    this.state = { stats: null, loading: true };
    this.fetchStats = this.fetchStats.bind(this);
  }

  componentDidMount() {
    this.fetchStats();
  }

  fetchStats() {
    var self = this;
    self.setState({ loading: true });
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/stats')
      .then(function(data) { self.setState({ stats: data, loading: false }); })
      .catch(function(err) { console.log('Failed to fetch stats:', err); self.setState({ loading: false }); });
  }

  render() {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var stats = this.state.stats;

    if (this.state.loading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
          <div className="tw-text-center">
            <i className="fa fa-circle-o-notch fa-spin tw-text-3xl tw-mb-3" style={{ color: '#D4A843', display: 'block' }} />
            <span className="tw-text-sm tw-text-[#999]">Loading...</span>
          </div>
        </div>
      );
    }

    if (!stats) return null;

    var revenue = stats.revenue || {};
    var txStats = stats.transactions || {};
    var breakdown = stats.payoutBreakdown || {};
    var platformRevenue = stats.platformRevenue || {};
    var dailyRevenue = stats.dailyRevenue || [];
    var methodBreakdown = stats.methodBreakdown || [];
    var recentTx = stats.recentTransactions || [];

    var pieData = [
      { name: 'Held', value: breakdown.held || 0 },
      { name: 'Released', value: breakdown.released || 0 },
      { name: 'Disputed', value: breakdown.disputed || 0 },
      { name: 'Refunded', value: breakdown.refunded || 0 },
    ].filter(function(d) { return d.value > 0; });

    var pieColors = ['#3B82F6', '#10B981', '#EF4444', '#6B7280'];

    var methodPieData = methodBreakdown.map(function(m) {
      return { name: (m.method || 'Unknown').charAt(0).toUpperCase() + (m.method || 'unknown').slice(1), value: m.total || 0 };
    });

    return (
      <div>
        {/* KPI Cards */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4 tw-mb-6">
          <KpiCard icon="fa-line-chart" label="Gross Platform Revenue" value={'$' + formatNum((platformRevenue.totalFees || 0) + (stats.transactionFeeRevenue || 0))} change={'$' + formatNum(platformRevenue.feesThisMonth) + ' commission this month'} color="#10B981" />
          <KpiCard icon="fa-dollar" label="Total GMV" value={'$' + formatNum(revenue.totalGMV)} change={txStats.today + ' today'} color="#D4A843" />
          <KpiCard icon="fa-exchange" label="Transactions" value={formatNum(txStats.total)} change={txStats.thisMonth + ' this month'} color="#3B82F6" />
          <KpiCard icon="fa-exclamation-circle" label="Active Disputes" value={stats.activeDisputes || 0} change={'$' + formatNum(revenue.disputedFunds) + ' at risk'} color="#EF4444" />
          <KpiCard icon="fa-lock" label="Held Funds" value={'$' + formatNum(revenue.heldFunds)} change={(breakdown.held || 0) + ' orders'} color="#8B5CF6" />
        </div>

        {/* Revenue Chart */}
        <div className="tw-rounded-xl tw-p-5 tw-mb-6" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <div>
              <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0">Sales Volume (Last 30 Days)</h3>
              <p className="tw-text-xs tw-text-[#999] tw-m-0 tw-mt-0.5">{txStats.thisMonth || 0} transactions this month</p>
            </div>
            <div className="tw-text-right">
              <span className="tw-text-xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">${formatNum(revenue.totalGMV)}</span>
            </div>
          </div>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <AreaChart data={dailyRevenue} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4A843" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#D4A843" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return v.slice(5); }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return '$' + v; }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                  itemStyle={{ color: '#fff' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={function(v) { return ['$' + Number(v).toFixed(2), 'Sales']; }}
                  labelFormatter={function(l) { return l; }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#D4A843" strokeWidth={2.5} fill="url(#revenueGrad)" dot={false} activeDot={{ r: 5, fill: '#D4A843' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Charts Row */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          {/* Payout Status Donut */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Payout Status</h3>
            {pieData.length > 0 ? (
              <div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                        {pieData.map(function(entry, index) {
                          return <Cell key={'cell-' + index} fill={pieColors[index % pieColors.length]} />;
                        })}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-3 tw-mt-2">
                  {pieData.map(function(d, i) {
                    return (
                      <span key={d.name} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-[#666] dark:tw-text-[#aaa]">
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: pieColors[i], display: 'inline-block' }} />
                        {d.name} ({d.value})
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data yet</div>
            )}
          </div>

          {/* Payment Methods */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Payment Methods</h3>
            {methodPieData.length > 0 ? (
              <div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <BarChart data={methodPieData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#999' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return '$' + v; }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} formatter={function(v) { return ['$' + Number(v).toFixed(2), 'Total']; }} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {methodPieData.map(function(entry, index) {
                          return <Cell key={'bar-' + index} fill={CHART_COLORS[index % CHART_COLORS.length]} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-3 tw-mt-2">
                  {methodPieData.map(function(d, i) {
                    return (
                      <span key={d.name} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-[#666] dark:tw-text-[#aaa]">
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: CHART_COLORS[i], display: 'inline-block' }} />
                        {d.name}: ${formatNum(d.value)}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data yet</div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Platform Stats</h3>
            <div className="tw-space-y-4">
              <div className="tw-text-[10px] tw-font-semibold tw-text-[#999] tw-uppercase tw-tracking-wider tw-mb-1">Earnings</div>
              <QuickStat icon="fa-trophy" label="Net Earnings" tooltip="Commission + Transaction Fees − Processor Fees" value={'$' + formatNum(((platformRevenue.totalFees || 0) + (stats.transactionFeeRevenue || 0) - (stats.estimatedProcessorFees || 0)))} color="#10B981" />
              <QuickStat icon="fa-percent" label="Take Rate" value={revenue.totalGMV > 0 ? (((platformRevenue.totalFees || 0) + (stats.transactionFeeRevenue || 0) - (stats.estimatedProcessorFees || 0)) / revenue.totalGMV * 100).toFixed(1) + '%' : '0%'} color="#10B981" />
              <QuickStat icon="fa-line-chart" label="Commission" value={'$' + formatNum(platformRevenue.totalFees)} color="#10B981" />
              <QuickStat icon="fa-credit-card-alt" label="Transaction Fees" value={'$' + formatNum(stats.transactionFeeRevenue || 0)} color="#10B981" />
              <QuickStat icon="fa-cogs" label="Est. Processor Fees" value={'-$' + formatNum(stats.estimatedProcessorFees || 0)} color="#EF4444" />
              <QuickStat icon="fa-share-alt" label="Marketer Payouts" value={'$' + formatNum(stats.marketerPayouts || 0)} color="#F59E0B" />
              <div className="tw-border-t tw-border-[#e8e8e8] dark:tw-border-[#333] tw-my-2" />
              <div className="tw-text-[10px] tw-font-semibold tw-text-[#999] tw-uppercase tw-tracking-wider tw-mb-1">Funds</div>
              <QuickStat icon="fa-check-circle" label="Released Funds" value={'$' + formatNum(revenue.releasedFunds)} color="#10B981" />
              <QuickStat icon="fa-undo" label="Refunded" value={'$' + formatNum(revenue.refundedFunds)} color="#6B7280" />
              <QuickStat icon="fa-balance-scale" label="Tax Collected" value={'$' + formatNum(stats.taxCollected || 0)} color="#6B7280" />
              <div className="tw-border-t tw-border-[#e8e8e8] dark:tw-border-[#333] tw-my-2" />
              <div className="tw-text-[10px] tw-font-semibold tw-text-[#999] tw-uppercase tw-tracking-wider tw-mb-1">Activity</div>
              <QuickStat icon="fa-users" label="Total Users" value={stats.activeUsers || 0} color="#3B82F6" />
              <QuickStat icon="fa-tag" label="Active Listings" value={stats.activeListings || 0} color="#10B981" />
              <QuickStat icon="fa-flag" label="Reported Listings" value={stats.reportedListings || 0} color="#EF4444" />
              <QuickStat icon="fa-credit-card" label="Stripe Transactions" value={txStats.stripe || 0} color="#8B5CF6" />
              <QuickStat icon="fa-paypal" label="PayPal Transactions" value={txStats.paypal || 0} color="#F59E0B" />
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        {recentTx.length > 0 && (
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Recent Activity</h3>
            <div className="tw-space-y-3">
              {recentTx.map(function(tx) {
                var postImg = tx.post && (resolveCardImage(tx.post.cardImage) || (tx.post.images && tx.post.images[0] && resolveCardImage(tx.post.images[0])));
                return (
                  <div key={tx._id} className="tw-flex tw-items-center tw-gap-3 tw-py-2 tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222] last:tw-border-0">
                    {postImg ? (
                      <img src={postImg} alt="" className="tw-w-10 tw-h-10 tw-rounded-lg tw-object-cover tw-flex-shrink-0" />
                    ) : (
                      <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-flex-shrink-0" style={{ backgroundColor: '#f0f0f0' }} />
                    )}
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-truncate">{tx.post ? tx.post.title : 'Deleted Post'}</div>
                      <div className="tw-text-xs tw-text-[#999]">
                        {tx.buyer ? tx.buyer.userName : 'Guest'} bought from {tx.seller ? tx.seller.userName : 'Unknown'}
                      </div>
                    </div>
                    <div className="tw-text-right tw-flex-shrink-0">
                      <div className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">${(tx.sellerPayout || 0).toFixed(2)}</div>
                      <StatusBadge status={tx.payoutStatus} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default AdminOverview;
